import { Card, ProgressBar } from '@blueprintjs/core';
import { TrackingEventList } from 'portal-sdk';
import * as React from 'react';
import { Body, Tooltip } from 'vet-bones/design-system/components';
import { sendHubspotEvent } from 'vet-bones/utils/portal';

import {
  ESSENTIALS_TRIPLES_LIMIT,
  FREE_TRIPLES_LIMIT,
} from 'src/ui/constants/billing';
import { ServerOverviewButton } from 'src/ui/containers/dashboard/ServerOverviewButton';
import { InstanceFlavors } from 'src/ui/features/connection';
import { Connection as GraphConnection } from 'src/ui/graph/types';
import { useServerOverview } from 'src/ui/hooks/overview';
import * as copy from 'src/ui/templates/copy';

const MetricWrapper: React.VFC<{
  tooltip: string;
  label: string;
  value: string;
}> = ({ tooltip, label, value }) => (
  <div className="metric">
    <Tooltip description={tooltip}>
      <Body size="lg">{label}</Body>
    </Tooltip>
    <Body size="lg" weight="semibold">
      {value}
    </Body>
  </div>
);

interface ServerOverviewProps {
  connection: GraphConnection | null | undefined;
}

export const ServerOverview: React.VFC<ServerOverviewProps> = ({
  connection,
}) => {
  const { data, isLoading } = useServerOverview(connection);
  const [hasSentEvent, setHasSentEvent] = React.useState(false);

  const isEssentials =
    connection?.cloud?.flavor?.name === InstanceFlavors.SMALL_VBX ||
    connection?.cloud?.flavor?.name === InstanceFlavors.SMALL;
  const isFree = connection?.isStardogFree;
  React.useEffect(() => {
    if (
      !hasSentEvent &&
      data?.numEdges &&
      data?.numDatabases &&
      !isLoading &&
      (isEssentials || isFree)
    ) {
      const totalEdges = isEssentials
        ? ESSENTIALS_TRIPLES_LIMIT
        : FREE_TRIPLES_LIMIT;
      const properties = {
        data_source_type: isFree ? 'free' : 'essentials',
        edge_count: data.numEdges.toString(),
        edges_remaining: (totalEdges - data.numEdges).toString(),
      };
      sendHubspotEvent(TrackingEventList.CLOUD_STORED_EDGES, properties);
      setHasSentEvent(true);
    }
  }, [data, hasSentEvent, isLoading, isEssentials, isFree]);

  if (isLoading || !data || !data.numDatabases) {
    return <Card className="metrics-card" />;
  }

  // Format the numbers so they are easier to read
  const formattedEdges = new Intl.NumberFormat().format(data.numEdges);
  const formattedNodes = new Intl.NumberFormat().format(data.numNodes);
  const formattedQueries = new Intl.NumberFormat().format(data.numQueries);
  const triplesLimit = isEssentials
    ? ESSENTIALS_TRIPLES_LIMIT
    : FREE_TRIPLES_LIMIT;
  const formattedTriplesLimit = new Intl.NumberFormat().format(triplesLimit);

  const {
    payment_system: paymentSystem,
    trial_days_remaining: trialDaysRemaining,
    trial_days_total: trialDaysTotal,
  } = connection?.stripeSubscriptionOrder || {};
  const isTrial =
    connection?.cloud?.flavor?.name === InstanceFlavors.SMALL_VBX &&
    paymentSystem === 'stripe_subscription' &&
    Number.isFinite(trialDaysRemaining) &&
    trialDaysTotal;

  return (
    <Card className="metrics-card">
      <Body className="metrics-card-header" size="sm" weight="semibold">
        {copy.components.ServerOverview.title.metrics}
      </Body>
      <div className="metrics-rows">
        {isTrial ? (
          <>
            <MetricWrapper
              label={
                copy.components.ServerOverview.metrics.trialDaysRemaining.label
              }
              tooltip={
                copy.components.ServerOverview.metrics.trialDaysRemaining
                  .description
              }
              value={copy.components.ServerOverview.metrics.trialDaysRemaining.days(
                trialDaysRemaining || 0
              )}
            />
            {trialDaysTotal ? (
              <ProgressBar
                animate={false}
                stripes={false}
                value={1 - (trialDaysRemaining || 0) / trialDaysTotal}
              />
            ) : null}
          </>
        ) : null}
        <MetricWrapper
          label={
            isEssentials || isFree
              ? copy.components.ServerOverview.labels.storedEdges
              : copy.components.ServerOverview.metrics.edges.label
          }
          tooltip={copy.components.ServerOverview.metrics.edges.description}
          value={formattedEdges}
        />
        {(isEssentials || isFree) && Number.isFinite(data.numEdges) ? (
          <>
            <Body size="md">{`${formattedEdges} of ${formattedTriplesLimit}`}</Body>
            <ProgressBar
              animate={false}
              stripes={false}
              value={data.numEdges / triplesLimit}
            />
          </>
        ) : null}
        <MetricWrapper
          label={copy.components.ServerOverview.metrics.nodes.label}
          tooltip={copy.components.ServerOverview.metrics.nodes.description}
          value={formattedNodes}
        />
        <MetricWrapper
          label={copy.components.ServerOverview.metrics.queries.label}
          tooltip={copy.components.ServerOverview.metrics.queries.description}
          value={formattedQueries}
        />
        {isEssentials || isFree ? (
          <MetricWrapper
            label={
              isEssentials
                ? copy.components.ServerOverview.metrics.hostingEssentials.label
                : copy.components.ServerOverview.metrics.hostingFree.label
            }
            tooltip={
              isEssentials
                ? copy.components.ServerOverview.metrics.hostingEssentials
                    .description
                : copy.components.ServerOverview.metrics.hostingFree.description
            }
            value={
              isEssentials
                ? copy.components.ServerOverview.metrics.hostingEssentials.value
                : copy.components.ServerOverview.metrics.hostingFree.value
            }
          />
        ) : null}
      </div>
      <div className="metrics-card-action">
        <ServerOverviewButton connection={connection} />
      </div>
    </Card>
  );
};
